import { useCallback, useMemo, useState } from 'react';
import { useLoginInput } from './use-login-input';
import { useToken } from './use-token';

export enum UploadType {
  AVATAR = 'avatar',
  CERTIFICATES = 'certificates',
}

export const useUploadDeleteUri = (
  type: UploadType,
): useUploadDeleteURIReturnType => {
  const host = process.env.REACT_APP_BACKEND;
  const token = useToken();

  if (!host) {
    throw new Error('Environment variable REACT_APP_BACKEND undefined!');
  }

  const { login } = useLoginInput();

  const uploadURI = useMemo(
    () => `${host}/upload/${login}/${type}`,
    [host, login, type],
  );
  const deleteURI = useMemo(
    () => `${host}/delete/${login}/${type}`,
    [host, login, type],
  );

  const [loading, setLoading] = useState(false);

  const uploadResource = useCallback(
    async (body: FormData) => {
      setLoading(true);

      const response = await fetch(uploadURI, {
        body,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        method: 'POST',
      });

      setLoading(false);
      if (!response.ok) {
        throw await response.json();
      }
    },
    [uploadURI, token],
  );

  const deleteResource = useCallback(async () => {
    if (type === UploadType.CERTIFICATES) {
      throw Error('Deleting certificates is not supported!');
    }

    setLoading(true);

    const response = await fetch(deleteURI, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      method: 'DELETE',
    });

    setLoading(false);
    if (!response.ok) {
      throw await response.json();
    }
  }, [deleteURI, type, token]);

  return {
    deleteResource,
    deleteURI,
    loading,
    uploadResource,
    uploadURI,
  };
};

type useUploadDeleteURIReturnType = {
  deleteResource: () => Promise<void>;
  deleteURI: string;

  uploadResource: (formDataValues: FormData) => Promise<void>;
  uploadURI: string;

  loading: boolean;
};
